import { useCallback, useEffect, useState } from 'react';
import { Copy, ExternalLink, Share2, Sparkles, X } from 'lucide-react';
import { STRINGS, type NbaAction } from '@maintenance-tracker/shared';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/card';
import { StatusBadge } from '@/components/ui/status-badge';
import { EarlyAccessModal } from '@/components/early-access-modal';
import { api } from '@/lib/utils';
import { qrToPngBlob } from '@/lib/qr';

type HomePayload = {
  greetingName: string;
  businessName: string;
  websiteUrl: string | null;
  onboardingComplete: boolean;
  actions: {
    primary: NbaAction;
    next: NbaAction[];
    homeState: string;
  };
  reviewStatus: {
    id: string;
    url: string;
    domain: string | null;
    status: string;
    finishedAt: string | null;
    createdAt: string;
    summaryCounts: Record<string, unknown>;
    topFindingTitle: string | null;
    failed: boolean;
    currentStepName: string | null;
  } | null;
  quoteShortcut: {
    slug: string;
    status: string;
    publicUrl: string;
    sharedOrLinked: boolean;
  } | null;
  recentInquiries: Array<{
    id: string;
    name: string;
    service: string | null;
    status: string;
    receivedAt: string;
  }>;
  comingSoon: { visible: boolean; snoozeUntil: string | null };
  homeOutcomesSeen: boolean;
  referral: { code: string | null; link: string | null; signupCount: number };
};

function greetingPrefix() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

function ActionButton({ action }: { action: NbaAction }) {
  return (
    <a href={action.href} className="inline-flex">
      <Button>{action.buttonLabel}</Button>
    </a>
  );
}

export function AppHomePage() {
  const [data, setData] = useState<HomePayload | null>(null);
  const [error, setError] = useState('');
  const [outcomesOpen, setOutcomesOpen] = useState(true);
  const [eaOpen, setEaOpen] = useState(false);
  const [copied, setCopied] = useState('');

  const load = useCallback(async () => {
    try {
      const payload = await api<HomePayload>('/api/v1/home');
      setData(payload);
      setOutcomesOpen(!payload.homeOutcomesSeen);
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load Home');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const snoozeComingSoon = async () => {
    await api('/api/v1/home/coming-soon/snooze', { method: 'POST' });
    setData((d) => (d ? { ...d, comingSoon: { ...d.comingSoon, visible: false } } : d));
  };

  const markOutcomesSeen = async () => {
    setOutcomesOpen(false);
    await api('/api/v1/home/outcomes/seen', { method: 'POST' }).catch(() => undefined);
  };

  const copyText = async (text: string, key: string) => {
    await navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(''), 2000);
  };

  const shareReferral = async () => {
    if (!data?.referral.link) return;
    if (navigator.share) {
      await navigator.share({
        title: STRINGS.product.name,
        text: STRINGS.home.referralLine,
        url: data.referral.link,
      });
    } else {
      await copyText(data.referral.link, 'ref');
    }
  };

  const downloadQuoteQr = async () => {
    if (!data?.quoteShortcut) return;
    const blob = await qrToPngBlob(data.quoteShortcut.publicUrl, 512);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `quote-qr-${data.quoteShortcut.slug}.png`;
    a.click();
    URL.revokeObjectURL(url);
    await api('/api/v1/home/quote-shared', { method: 'POST', body: JSON.stringify({}) }).catch(
      () => undefined,
    );
  };

  if (error && !data) {
    return (
      <div className="mx-auto max-w-2xl rounded-xl border border-border bg-bg-surface p-6">
        <p className="text-sm text-text-secondary">{error}</p>
        <Button className="mt-4" onClick={() => void load()}>
          Retry
        </Button>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="mx-auto max-w-2xl space-y-4">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  const { primary, next, homeState } = data.actions;
  const hideSecondary = homeState === 'review_running';

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div>
        <h2 className="text-2xl font-semibold tracking-tight text-text-primary">
          {greetingPrefix()}, {data.greetingName}.
        </h2>
        <p className="mt-1 text-sm text-text-secondary">{data.businessName}</p>
      </div>

      {/* Primary action */}
      <section className="rounded-2xl border border-border bg-bg-surface p-6 shadow-card">
        <h3 className="text-lg font-semibold text-text-primary">{primary.title}</h3>
        <p className="mt-2 text-sm text-text-secondary">{primary.why}</p>
        {primary.estimatedMinutes != null && (
          <p className="mt-1 text-xs text-text-muted">About {primary.estimatedMinutes} minutes</p>
        )}
        <div className="mt-4 flex flex-wrap gap-2">
          <ActionButton action={primary} />
          {primary.id === 'add_website' && primary.meta?.secondaryHref && (
            <a href={String(primary.meta.secondaryHref)}>
              <Button variant="secondary">{String(primary.meta.secondaryLabel)}</Button>
            </a>
          )}
          {primary.id === 'retry_failed_review' && (
            <>
              <a href="/app/review">
                <Button variant="secondary">Check the address</Button>
              </a>
              <a href="/app/quote-requests">
                <Button variant="ghost">Continue with other kits</Button>
              </a>
            </>
          )}
        </div>
      </section>

      {/* Outcomes — first visit */}
      {!hideSecondary && (
        <details
          className="rounded-xl border border-border bg-bg-muted/40 px-4 py-3"
          open={outcomesOpen}
          onToggle={(e) => {
            if (!(e.target as HTMLDetailsElement).open) void markOutcomesSeen();
          }}
        >
          <summary className="cursor-pointer text-sm font-medium text-text-primary">
            What this will help you do
          </summary>
          <ul className="mt-3 space-y-2 text-sm text-text-secondary">
            <li>{STRINGS.home.outcomesFind}</li>
            <li>{STRINGS.home.outcomesFix}</li>
            <li>{STRINGS.home.outcomesWin}</li>
          </ul>
        </details>
      )}

      {/* Review status */}
      {!hideSecondary && data.reviewStatus && !data.reviewStatus.failed && (
        <section className="rounded-xl border border-border bg-bg-surface p-5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="font-semibold text-text-primary">Website review</h3>
              <p className="mt-1 text-sm text-text-secondary">
                {data.reviewStatus.domain || data.reviewStatus.url}
                {data.reviewStatus.finishedAt
                  ? ` · ${new Date(data.reviewStatus.finishedAt).toLocaleDateString()}`
                  : ''}
              </p>
            </div>
            <StatusBadge status={data.reviewStatus.status} />
          </div>
          <div className="mt-3 flex flex-wrap gap-3 text-sm text-text-secondary">
            {typeof data.reviewStatus.summaryCounts.needsAttention === 'number' && (
              <span>Needs attention: {String(data.reviewStatus.summaryCounts.needsAttention)}</span>
            )}
            {typeof data.reviewStatus.summaryCounts.looksGood === 'number' && (
              <span>Looks good: {String(data.reviewStatus.summaryCounts.looksGood)}</span>
            )}
            {typeof data.reviewStatus.summaryCounts.couldNotCheck === 'number' && (
              <span>Could not check: {String(data.reviewStatus.summaryCounts.couldNotCheck)}</span>
            )}
          </div>
          {data.reviewStatus.topFindingTitle && (
            <p className="mt-2 text-sm text-text-primary">Top: {data.reviewStatus.topFindingTitle}</p>
          )}
          <div className="mt-4 flex flex-wrap gap-2">
            <a href={`/app/review/${data.reviewStatus.id}`}>
              <Button size="sm">View report</Button>
            </a>
            <a href="/app/review">
              <Button size="sm" variant="secondary">
                Re-run review
              </Button>
            </a>
          </div>
        </section>
      )}

      {/* Next actions */}
      {!hideSecondary && next.length > 0 && (
        <section className="space-y-3">
          <h3 className="text-sm font-medium uppercase tracking-wide text-text-muted">Next actions</h3>
          {next.map((a) => (
            <div
              key={`${a.id}-${a.href}`}
              className="flex flex-col gap-3 rounded-xl border border-border bg-bg-surface p-4 sm:flex-row sm:items-center sm:justify-between"
            >
              <div>
                <p className="font-medium text-text-primary">{a.title}</p>
                <p className="mt-1 text-sm text-text-secondary">{a.why}</p>
                {a.estimatedMinutes != null && (
                  <p className="mt-1 text-xs text-text-muted">~{a.estimatedMinutes} min</p>
                )}
              </div>
              <a href={a.href} className="shrink-0">
                <Button size="sm" variant="secondary">
                  {a.buttonLabel}
                </Button>
              </a>
            </div>
          ))}
        </section>
      )}

      {/* Quote shortcut */}
      {!hideSecondary && data.quoteShortcut && (
        <section className="rounded-xl border border-border bg-bg-surface p-5">
          <div className="flex items-center justify-between gap-2">
            <h3 className="font-semibold text-text-primary">Quote request page</h3>
            <StatusBadge status={data.quoteShortcut.status === 'live' ? 'Live' : data.quoteShortcut.status} />
          </div>
          <p className="mt-2 break-all text-sm text-text-secondary">{data.quoteShortcut.publicUrl}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => void copyText(data.quoteShortcut!.publicUrl, 'quote').then(() =>
                api('/api/v1/home/quote-shared', { method: 'POST', body: JSON.stringify({}) }),
              )}
            >
              <Copy className="mr-1 h-3.5 w-3.5" />
              {copied === 'quote' ? 'Copied' : 'Copy'}
            </Button>
            <a href={data.quoteShortcut.publicUrl} target="_blank" rel="noreferrer">
              <Button size="sm" variant="secondary">
                <ExternalLink className="mr-1 h-3.5 w-3.5" />
                Open
              </Button>
            </a>
            <Button size="sm" variant="secondary" onClick={() => void downloadQuoteQr()}>
              QR download
            </Button>
          </div>
        </section>
      )}

      {/* Recent inquiries — only if real */}
      {!hideSecondary && data.recentInquiries.length > 0 && (
        <section className="rounded-xl border border-border bg-bg-surface p-5">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-text-primary">Recent inquiries</h3>
            <a href="/app/quote-requests" className="text-sm font-medium text-brand hover:underline">
              View all
            </a>
          </div>
          <ul className="mt-3 divide-y divide-border">
            {data.recentInquiries.map((i) => (
              <li key={i.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                <div>
                  <p className="font-medium text-text-primary">{i.name}</p>
                  <p className="text-text-secondary">
                    {i.service || 'Request'} · {new Date(i.receivedAt).toLocaleString()}
                  </p>
                </div>
                <StatusBadge status={i.status} />
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Coming soon strip */}
      {!hideSecondary && data.comingSoon.visible && (
        <section className="relative rounded-xl border border-dashed border-border bg-bg-muted/30 px-5 py-4">
          <button
            type="button"
            className="absolute right-3 top-3 rounded-md p-1 text-text-muted hover:bg-bg-subtle"
            aria-label="Dismiss for 30 days"
            onClick={() => void snoozeComingSoon()}
          >
            <X className="h-4 w-4" />
          </button>
          <div className="flex items-start gap-3 pr-6">
            <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-text-muted" />
            <div>
              <p className="text-sm text-text-secondary">{STRINGS.home.comingSoonStrip}</p>
              <Button size="sm" className="mt-3" onClick={() => setEaOpen(true)}>
                Get early access
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* Referral footer */}
      {data.referral.link && (
        <footer className="border-t border-border pt-4 text-center">
          <p className="text-[13px] text-text-muted">{STRINGS.home.referralLine}</p>
          <div className="mt-3 flex flex-wrap justify-center gap-2">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => void copyText(data.referral.link!, 'ref')}
            >
              <Copy className="mr-1 h-3.5 w-3.5" />
              {copied === 'ref' ? 'Copied' : 'Copy link'}
            </Button>
            <Button size="sm" variant="secondary" onClick={() => void shareReferral()}>
              <Share2 className="mr-1 h-3.5 w-3.5" />
              Share
            </Button>
          </div>
        </footer>
      )}

      <EarlyAccessModal
        open={eaOpen}
        onClose={() => setEaOpen(false)}
        feature="website_insights"
        title="Early access"
        description="Tell us what you want first — Search Console, Analytics, AI help, or social."
      />
    </div>
  );
}
